import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/warehouses';

export const useWarehouses = () =>
  useQuery({ queryKey: ['warehouses'], queryFn: () => api.getWarehouses() });

export const useWarehouse = (uuid) =>
  useQuery({
    queryKey: ['warehouses', uuid],
    queryFn: () => api.getWarehouse(uuid),
    enabled: !!uuid,
  });

export const useCreateWarehouse = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createWarehouse(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warehouses'] }),
  });
};

export const useUpdateWarehouse = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }) => api.updateWarehouse(uuid, data),
    onSuccess: (_, { uuid }) => {
      qc.invalidateQueries({ queryKey: ['warehouses'] });
      qc.invalidateQueries({ queryKey: ['warehouses', uuid] });
    },
  });
};

export const useDeactivateWarehouse = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, businessUuid }) =>
      api.deactivateWarehouse(uuid, businessUuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warehouses'] }),
  });
};

export const useActivateWarehouse = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, businessUuid }) =>
      api.activateWarehouse(uuid, businessUuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warehouses'] }),
  });
};

export const useDeleteWarehouse = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, businessUuid }) =>
      api.deleteWarehouse(uuid, businessUuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warehouses'] }),
  });
};

export const useLocations = (warehouseUuid) =>
  useQuery({
    queryKey: ['locations', warehouseUuid],
    queryFn: () => api.getLocations(warehouseUuid),
    enabled: !!warehouseUuid,
  });

export const useCreateLocation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ warehouseUuid, data }) =>
      api.createLocation(warehouseUuid, data),
    onSuccess: (_, { warehouseUuid }) =>
      qc.invalidateQueries({ queryKey: ['locations', warehouseUuid] }),
  });
};

export const useUpdateLocation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }) => api.updateLocation(uuid, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  });
};

export const useDeactivateLocation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, businessUuid }) =>
      api.deactivateLocation(uuid, businessUuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  });
};

export const useActivateLocation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, businessUuid }) =>
      api.activateLocation(uuid, businessUuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  });
};

export const useDeleteLocation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, businessUuid }) =>
      api.deleteLocation(uuid, businessUuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  });
};

export const useStockSummary = (warehouseUuid) =>
  useQuery({
    queryKey: ['stock-summary', warehouseUuid],
    queryFn: () => api.getStockSummary(warehouseUuid),
    enabled: !!warehouseUuid,
  });
